import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, serverTimestamp, collection, addDoc, deleteDoc, getDocs, query, orderBy } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * UseCaseFinder
 *
 * A self-serve intake that pulls a person's real work out of them, then hands
 * back AI use cases built for their role. Output is split into two tiers:
 *   - Quick wins they can run today in a chat window, no building
 *   - Bigger plays worth building an agent for, each with an agent idea
 *
 * The Claude call happens server side in the findUseCases Cloud Function, the
 * same secure pattern the Final Exam uses. No API key in the browser.
 *
 * Expected response from findUseCases:
 * {
 *   superficial: [ { name, what, systems, taskType, firstStep } ],
 *   significant: [ { name, what, systems, taskType, why, impact,
 *                    agentIdea: { does, trigger, needs } } ]
 * }
 */

const SYSTEMS = [
  'Email (Outlook or Gmail)',
  'Spreadsheets (Excel or Sheets)',
  'Documents (Word or Docs)',
  'Slack or Teams',
  'Zoom or video calls',
  'A CRM (Salesforce, HubSpot, etc.)',
  'Project tool (Asana, Monday, Trello)',
  'Calendar and scheduling',
  'Accounting (QuickBooks, etc.)',
  'Presentations (PowerPoint, Slides)',
  'Forms or surveys',
  'A database or internal system',
];

const TASK_PROMPTS = [
  { key: 'repetitive', label: 'Things you do over and over, the same way each time', placeholder: 'Example. Every Monday I pull numbers from three reports and paste them into one summary.' },
  { key: 'communication', label: 'Things you write or respond to regularly', placeholder: 'Example. I answer the same five customer questions by email all week.' },
  { key: 'research', label: 'Things you look up, compile, or pull together', placeholder: 'Example. I research vendors and put their pricing into a comparison.' },
  { key: 'synthesis', label: 'Things you summarize, analyze, or turn into a decision', placeholder: 'Example. I read meeting notes and write up what we decided and who owns what.' },
];

export default function UseCaseFinder({ user, onNav }) {
  const [view, setView] = useState('list'); // 'list' = saved runs, 'finder' = intake + results
  const [step, setStep] = useState(0);
  const [role, setRole] = useState({ title: '', department: '', responsibility: '' });
  const [systems, setSystems] = useState([]);
  const [otherSystem, setOtherSystem] = useState('');
  const [tasks, setTasks] = useState({ repetitive: '', communication: '', research: '', synthesis: '' });
  const [drain, setDrain] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [savedRuns, setSavedRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [profile, setProfile] = useState({ title: '', department: '' });

  // Load the person's profile (for intake prefill) and their list of saved
  // runs. Land on the list. If they have nothing saved yet, an empty list is a
  // dead end, so drop them straight into a fresh intake instead.
  useEffect(() => {
    const load = async () => {
      setRunsLoading(true);
      let prefillTitle = '';
      let prefillDept = '';
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists()) {
          const d = snap.data();
          prefillTitle = d.jobTitle || '';
          prefillDept = d.department || '';
          setProfile({ title: prefillTitle, department: prefillDept });
        }
      } catch {}
      try {
        const runsQ = query(
          collection(db, 'useCaseFinder', user.uid, 'runs'),
          orderBy('savedAt', 'desc')
        );
        const runsSnap = await getDocs(runsQ);
        const runs = runsSnap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
        setSavedRuns(runs);
        if (runs.length === 0) {
          setRole({ title: prefillTitle, department: prefillDept, responsibility: '' });
          setView('finder');
        }
      } catch {
        // If the list cannot load, do not strand the person. Let them start fresh.
        setRole({ title: prefillTitle, department: prefillDept, responsibility: '' });
        setView('finder');
      } finally {
        setRunsLoading(false);
      }
    };
    load();
  }, [user.uid]);

  // Build an auto title from job title and the save date, e.g. "Office Manager, June 14, 2026"
  const buildTitle = (titleText) => {
    const t = (titleText || '').trim() || 'My role';
    const d = new Date();
    const date = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    return `${t}, ${date}`;
  };

  const toggleSystem = (sName) =>
    setSystems(prev => (prev.includes(sName) ? prev.filter(x => x !== sName) : [...prev, sName]));

  const allSystems = otherSystem.trim() ? [...systems, otherSystem.trim()] : systems;
  const taskHasSomething = Object.values(tasks).some(t => t.trim().length > 0);

  const generate = async () => {
    setLoading(true);
    setError('');
    setResults(null);
    setSavedAt(null);
    try {
      const functions = getFunctions(undefined, 'us-central1');
      const findUseCases = httpsCallable(functions, 'findUseCases');
      const res = await findUseCases({
        intake: {
          title: role.title,
          department: role.department,
          responsibility: role.responsibility,
          systems: allSystems,
          tasks,
          drain,
        },
      });
      const data = res.data || {};
      setResults({
        superficial: data.superficial || [],
        significant: data.significant || [],
      });
      setStep(4);
    } catch (e) {
      setError('Something went sideways building your use cases. Give it another try.');
    } finally {
      setLoading(false);
    }
  };

  const saveResults = async () => {
    if (!results) return;
    setSaving(true);
    setError('');
    try {
      const runTitle = buildTitle(role.title);
      const payload = {
        title: runTitle,
        intake: {
          title: role.title,
          department: role.department,
          responsibility: role.responsibility,
          systems: allSystems,
          tasks,
          drain,
        },
        results: {
          superficial: results.superficial || [],
          significant: results.significant || [],
        },
        savedAt: serverTimestamp(),
      };
      const ref = await addDoc(collection(db, 'useCaseFinder', user.uid, 'runs'), payload);
      // Reflect the new run at the top of the list immediately, using local
      // time so the row reads correctly before the server timestamp resolves.
      setSavedRuns(prev => [{ id: ref.id, ...payload, savedAt: Date.now() }, ...prev]);
      setSavedAt(Date.now());
    } catch (e) {
      setError('Could not save your results. Give it another try.');
    } finally {
      setSaving(false);
    }
  };

  // Open a saved run into the full results view
  const openRun = (run) => {
    setResults({
      superficial: run.results?.superficial || [],
      significant: run.results?.significant || [],
    });
    if (run.intake) {
      setRole({
        title: run.intake.title || '',
        department: run.intake.department || '',
        responsibility: run.intake.responsibility || '',
      });
      setSystems(Array.isArray(run.intake.systems) ? run.intake.systems : []);
      setOtherSystem('');
      setTasks({
        repetitive: run.intake.tasks?.repetitive || '',
        communication: run.intake.tasks?.communication || '',
        research: run.intake.tasks?.research || '',
        synthesis: run.intake.tasks?.synthesis || '',
      });
      setDrain(run.intake.drain || '');
    }
    setSavedAt(run.savedAt || true); // already saved, so show "Save again"
    setError('');
    setStep(4);
    setView('finder');
  };

  // Delete a saved run from the list and Firestore
  const deleteRun = async (runId) => {
    try {
      await deleteDoc(doc(db, 'useCaseFinder', user.uid, 'runs', runId));
      setSavedRuns(prev => prev.filter(r => r.id !== runId));
    } catch {
      setError('Could not delete that run. Give it another try.');
    }
  };

  // Start a fresh intake, prefilled from the profile
  const startNew = () => {
    setStep(0);
    setRole({ title: profile.title || '', department: profile.department || '', responsibility: '' });
    setSystems([]);
    setOtherSystem('');
    setTasks({ repetitive: '', communication: '', research: '', synthesis: '' });
    setDrain('');
    setResults(null);
    setError('');
    setSavedAt(null);
    setView('finder');
  };

  // Return to the saved-runs list
  const backToList = () => {
    setResults(null);
    setError('');
    setSavedAt(null);
    setStep(0);
    setView('list');
  };

  const canNext =
    (step === 0 && role.title.trim()) ||
    (step === 1 && allSystems.length > 0) ||
    (step === 2 && taskHasSomething) ||
    step === 3;

  return (
    <div style={s.wrapper}>
      <div style={s.header}>
        <div style={s.dayTag}>From Curious to Capable</div>
        <div style={s.title}>Find Where AI Fits Your Work</div>
        <div style={s.sub}>No idea where to start? Tell us what your week looks like. We hand back use cases built for you.</div>
      </div>

      {view === 'list' && (
        <div style={s.body}>
          {runsLoading ? (
            <div style={s.card}>
              <p style={s.helper}>Loading your saved runs...</p>
            </div>
          ) : (
            <div>
              <div style={s.listHead}>
                <div>
                  <div style={s.listTitle}>Your saved runs</div>
                  <div style={s.helper}>Open any run to see the use cases you saved. Start a new one anytime.</div>
                </div>
                <button onClick={startNew} style={s.newBtn}>Start a new one</button>
              </div>

              {savedRuns.length === 0 ? (
                <div style={s.emptyCard}>
                  <div style={s.emptyTitle}>Nothing saved yet</div>
                  <p style={s.helper}>Run the finder and save a set to see it here.</p>
                  <button onClick={startNew} style={{ ...s.primaryBtn, width: 'auto', marginTop: '14px' }}>Find my use cases</button>
                </div>
              ) : (
                savedRuns.map(run => {
                  const qCount = (run.results?.superficial || []).length;
                  const aCount = (run.results?.significant || []).length;
                  return (
                    <div key={run.id} style={s.runRow}>
                      <button onClick={() => openRun(run)} style={s.runMain}>
                        <div style={s.runName}>{run.title || 'Saved run'}</div>
                        <div style={s.runMeta}>
                          {qCount} quick {qCount === 1 ? 'win' : 'wins'}, {aCount} worth building an agent for
                        </div>
                      </button>
                      <button onClick={() => deleteRun(run.id)} style={s.deleteBtn} aria-label="Delete this run">Delete</button>
                    </div>
                  );
                })
              )}
              {error && <div style={{ ...s.error, marginTop: '14px' }}>{error}</div>}
            </div>
          )}
        </div>
      )}

      {view === 'finder' && step < 4 && (
        <div style={s.progressBar}>
          {[0, 1, 2, 3].map(i => (
            <div key={i} style={{ ...s.progressSeg, background: i <= step ? '#D4A847' : '#E8E6E2' }} />
          ))}
        </div>
      )}

      {view === 'finder' && (
      <div style={s.body}>
        {savedRuns.length > 0 && (
          <button onClick={backToList} style={s.backToListBtn}>Back to saved runs</button>
        )}
        {step === 0 && (
          <div style={s.card}>
            <StepTitle n="1" text="Start with your role" />
            <Field label="Your job title">
              <input style={s.input} value={role.title} placeholder="Office Manager"
                onChange={e => setRole({ ...role, title: e.target.value })} />
            </Field>
            <Field label="Your department or team">
              <input style={s.input} value={role.department} placeholder="Operations"
                onChange={e => setRole({ ...role, department: e.target.value })} />
            </Field>
            <Field label="In one line, what are you responsible for?">
              <input style={s.input} value={role.responsibility} placeholder="Keeping the office running and supporting the leadership team"
                onChange={e => setRole({ ...role, responsibility: e.target.value })} />
            </Field>
          </div>
        )}

        {step === 1 && (
          <div style={s.card}>
            <StepTitle n="2" text="What do you actually open all day?" />
            <p style={s.helper}>Tap everything you use. The boring ones count the most.</p>
            <div style={s.chipWrap}>
              {SYSTEMS.map(sName => {
                const on = systems.includes(sName);
                return (
                  <button key={sName} onClick={() => toggleSystem(sName)}
                    style={{ ...s.chip, ...(on ? s.chipOn : {}) }}>
                    {sName}
                  </button>
                );
              })}
            </div>
            <Field label="Anything else you use a lot?">
              <input style={s.input} value={otherSystem} placeholder="Type a tool and it gets added"
                onChange={e => setOtherSystem(e.target.value)} />
            </Field>
          </div>
        )}

        {step === 2 && (
          <div style={s.card}>
            <StepTitle n="3" text="What eats your week?" />
            <p style={s.helper}>You do not have to fill in all four. Write where it is true for you.</p>
            {TASK_PROMPTS.map(tp => (
              <Field key={tp.key} label={tp.label}>
                <textarea rows={2} style={{ ...s.input, resize: 'vertical', minHeight: '60px', lineHeight: '1.6' }}
                  value={tasks[tp.key]} placeholder={tp.placeholder}
                  onChange={e => setTasks({ ...tasks, [tp.key]: e.target.value })} />
              </Field>
            ))}
            <Field label="Of everything above, what wastes the most time or annoys you most?">
              <input style={s.input} value={drain} placeholder="The thing you wish would just do itself"
                onChange={e => setDrain(e.target.value)} />
            </Field>
          </div>
        )}

        {step === 3 && (
          <div style={s.card}>
            <StepTitle n="4" text="Ready when you are" />
            <p style={s.helper}>
              We will read what you do and hand back two tiers. Quick wins you can try today, and bigger plays worth building an agent for.
            </p>
            {error && <div style={s.error}>{error}</div>}
            <button onClick={generate} disabled={loading} style={s.primaryBtn}>
              {loading ? 'Building your use cases...' : 'Show me my use cases'}
            </button>
          </div>
        )}

        {step === 4 && results && (
          <div>
            <div style={s.resultsIntro}>
              <div style={s.resultsTitle}>Your AI use cases</div>
              <div style={s.helper}>Two tiers. Quick wins you can do today, and bigger plays worth building an agent for.</div>
              <div style={s.saveRow}>
                <button onClick={saveResults} disabled={saving} style={{ ...s.saveBtn, opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Saving...' : savedAt ? 'Save again' : 'Save my results'}
                </button>
                {savedAt && <span style={s.savedNote}>Saved. They will be here when you come back.</span>}
              </div>
              {error && <div style={s.error}>{error}</div>}
            </div>

            <div style={s.tierHead}>
              <span style={s.tierName}>Quick wins</span>
              <span style={s.tierNote}>Do these today in a chat window. No building.</span>
            </div>
            {(results.superficial || []).map((r, i) => (
              <div key={i} style={s.quickCard}>
                <div style={s.cardName}>{r.name}</div>
                <p style={s.cardWhat}>{r.what}</p>
                <div style={s.tagRow}>
                  <Tag dark text={r.taskType} />
                  <Tag text="Quick win" />
                </div>
                <Line label="Touches" value={r.systems} />
                <div style={s.firstStepBox}>
                  <div style={s.firstStepLabel}>Try this first</div>
                  <div style={s.firstStepText}>{r.firstStep}</div>
                </div>
              </div>
            ))}

            <div style={{ ...s.tierHead, marginTop: '32px' }}>
              <span style={s.tierName}>Worth building an agent for</span>
              <span style={s.tierNote}>These repeat or chain enough that an agent should own them.</span>
            </div>
            {(results.significant || []).map((r, i) => (
              <div key={i} style={s.agentCard}>
                <div style={s.agentCardTop}>
                  <div style={s.cardName}>{r.name}</div>
                  <span style={s.impactBadge}>IMPACT {String(r.impact || '').toUpperCase()}</span>
                </div>
                <p style={s.cardWhat}>{r.what}</p>
                <div style={s.tagRow}>
                  <Tag dark text={r.taskType} />
                </div>
                <Line label="Touches" value={r.systems} />
                <Line label="Why build it" value={r.why} />
                {r.agentIdea && (
                  <div style={s.agentBox}>
                    <div style={s.agentBoxLabel}>Agent idea</div>
                    <AgentLine label="What it does" value={r.agentIdea.does} />
                    <AgentLine label="What kicks it off" value={r.agentIdea.trigger} />
                    <AgentLine label="What it needs" value={r.agentIdea.needs} />
                  </div>
                )}
              </div>
            ))}

            <div style={s.resultsFooter}>
              <button onClick={startNew} style={{ ...s.primaryBtn, width: 'auto', marginTop: 0 }}>Start a new one</button>
              <button onClick={backToList} style={s.ghostBtn}>Back to saved runs</button>
            </div>
          </div>
        )}

        {step < 4 && (
          <div style={s.navRow}>
            <button onClick={() => setStep(step - 1)} disabled={step === 0}
              style={{ ...s.ghostBtn, opacity: step === 0 ? 0.4 : 1 }}>
              Back
            </button>
            {step < 3 && (
              <button onClick={() => setStep(step + 1)} disabled={!canNext}
                style={{ ...s.primaryBtn, width: 'auto', opacity: canNext ? 1 : 0.5 }}>
                Next
              </button>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
}

function StepTitle({ n, text }) {
  return (
    <div style={s.stepTitleRow}>
      <div style={s.stepNum}>{n}</div>
      <div style={s.stepTitleText}>{text}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div style={{ marginTop: '16px' }}>
      <label style={s.fieldLabel}>{label}</label>
      {children}
    </div>
  );
}
function Tag({ text, dark }) {
  return <span style={{ ...s.tag, ...(dark ? s.tagDark : {}) }}>{text}</span>;
}
function Line({ label, value }) {
  return (
    <div style={s.lineRow}>
      <span style={s.lineLabel}>{label}</span>
      <span style={s.lineValue}>{value}</span>
    </div>
  );
}
function AgentLine({ label, value }) {
  return (
    <div style={s.agentLineRow}>
      <span style={s.agentLineLabel}>{label}</span>
      <span style={s.agentLineValue}>{value}</span>
    </div>
  );
}

const s = {
  wrapper: { maxWidth: '820px', paddingBottom: '60px', fontFamily: 'DM Sans, sans-serif' },

  header: { background: '#1B2A4A', padding: '28px 32px' },
  dayTag: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  title: { fontFamily: 'Playfair Display, serif', fontSize: '26px', color: '#fff', marginBottom: '8px', lineHeight: '1.2' },
  sub: { fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: '300', lineHeight: '1.6', maxWidth: '560px' },

  progressBar: { display: 'flex', gap: '6px', background: '#FFFFFF', borderLeft: '1.5px solid #E8E6E2', borderRight: '1.5px solid #E8E6E2', padding: '16px 32px' },
  progressSeg: { flex: 1, height: '5px', borderRadius: '5px', transition: 'background 0.3s' },

  body: { background: '#FAF8F5', border: '1.5px solid #E8E6E2', borderTop: 'none', padding: '28px 32px' },

  card: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '28px' },

  stepTitleRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' },
  stepNum: { background: '#1B2A4A', color: '#fff', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Playfair Display, serif', fontWeight: '700', flexShrink: 0 },
  stepTitleText: { fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '700', color: '#1B2A4A' },

  helper: { fontSize: '13px', color: '#8A8A8A', fontWeight: '300', lineHeight: '1.6', marginTop: '4px' },
  fieldLabel: { display: 'block', fontSize: '11px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#8A8A8A', marginBottom: '6px' },
  input: { width: '100%', border: '1.5px solid #E8E6E2', padding: '11px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', boxSizing: 'border-box' },

  chipWrap: { display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '14px' },
  chip: { border: '1.5px solid #E8E6E2', background: '#fff', color: '#1B2A4A', padding: '10px 14px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  chipOn: { background: '#1B2A4A', color: '#fff', borderColor: '#1B2A4A' },

  primaryBtn: { width: '100%', background: '#1B2A4A', color: '#fff', border: 'none', padding: '14px 24px', fontFamily: 'Playfair Display, serif', fontSize: '15px', fontWeight: '700', cursor: 'pointer', marginTop: '14px' },
  ghostBtn: { background: 'transparent', color: '#1B2A4A', border: '1.5px solid #1B2A4A', padding: '11px 24px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  navRow: { display: 'flex', justifyContent: 'space-between', marginTop: '20px' },

  error: { background: '#fdecea', color: '#a32d2d', padding: '12px 14px', fontSize: '13px', marginTop: '12px' },

  resultsIntro: { marginBottom: '20px' },
  resultsTitle: { fontFamily: 'Playfair Display, serif', fontSize: '22px', fontWeight: '900', color: '#1B2A4A', marginBottom: '4px' },
  saveRow: { display: 'flex', alignItems: 'center', gap: '14px', marginTop: '16px', flexWrap: 'wrap' },
  saveBtn: { background: '#D4A847', color: '#1B2A4A', border: 'none', padding: '11px 22px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  savedNote: { fontSize: '13px', color: '#5A7A4A', fontWeight: '600' },

  listHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '18px', flexWrap: 'wrap' },
  listTitle: { fontFamily: 'Playfair Display, serif', fontSize: '22px', fontWeight: '900', color: '#1B2A4A', marginBottom: '4px' },
  newBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '12px 22px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  emptyCard: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '32px 28px', textAlign: 'center' },
  emptyTitle: { fontFamily: 'Playfair Display, serif', fontSize: '18px', fontWeight: '700', color: '#1B2A4A', marginBottom: '6px' },
  runRow: { display: 'flex', alignItems: 'stretch', gap: '0', background: '#FFFFFF', border: '1.5px solid #E8E6E2', marginBottom: '10px' },
  runMain: { flex: 1, textAlign: 'left', background: 'transparent', border: 'none', padding: '18px 20px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  runName: { fontFamily: 'Playfair Display, serif', fontSize: '16px', fontWeight: '700', color: '#1B2A4A', marginBottom: '4px' },
  runMeta: { fontSize: '13px', color: '#8A8A8A', fontWeight: '300' },
  deleteBtn: { background: 'transparent', border: 'none', borderLeft: '1.5px solid #E8E6E2', color: '#A35A4A', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: '600', padding: '0 20px', cursor: 'pointer', flexShrink: 0 },
  backToListBtn: { background: 'transparent', border: 'none', color: '#1B2A4A', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: '700', cursor: 'pointer', padding: '0 0 14px', textDecoration: 'underline' },
  resultsFooter: { display: 'flex', gap: '12px', marginTop: '24px', flexWrap: 'wrap' },

  tierHead: { display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' },
  tierName: { fontFamily: 'Playfair Display, serif', fontSize: '17px', fontWeight: '700', color: '#1B2A4A' },
  tierNote: { fontSize: '12px', color: '#8A8A8A' },

  quickCard: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px', margin: '12px 0' },
  agentCard: { background: '#FFFFFF', border: '2px solid #D4A847', padding: '22px', margin: '12px 0' },
  agentCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' },

  cardName: { fontFamily: 'Playfair Display, serif', fontSize: '17px', fontWeight: '700', color: '#1B2A4A' },
  cardWhat: { margin: '8px 0 12px', fontSize: '14px', color: '#3D3D3D', lineHeight: '1.6', fontWeight: '300' },

  tagRow: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' },
  tag: { background: '#F2F0EC', color: '#1B2A4A', fontSize: '11px', fontWeight: '700', padding: '5px 10px', letterSpacing: '0.5px' },
  tagDark: { background: '#243560', color: '#fff' },

  impactBadge: { background: '#D4A847', color: '#1B2A4A', fontWeight: '700', fontSize: '10px', padding: '4px 10px', whiteSpace: 'nowrap', letterSpacing: '0.5px' },

  lineRow: { display: 'flex', gap: '8px', fontSize: '13px', marginTop: '6px' },
  lineLabel: { fontWeight: '700', color: '#1B2A4A', minWidth: '80px' },
  lineValue: { color: '#3D3D3D', fontWeight: '300' },

  firstStepBox: { background: '#FAF8F5', border: '1px solid #E8E6E2', padding: '12px 14px', marginTop: '12px' },
  firstStepLabel: { fontSize: '11px', fontWeight: '700', color: '#8A6F2A', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' },
  firstStepText: { fontSize: '13px', color: '#1B2A4A', lineHeight: '1.6' },

  agentBox: { background: '#1B2A4A', padding: '16px 18px', marginTop: '14px' },
  agentBoxLabel: { fontSize: '11px', fontWeight: '700', color: '#D4A847', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' },
  agentLineRow: { display: 'flex', gap: '10px', fontSize: '13px', marginTop: '7px' },
  agentLineLabel: { fontWeight: '700', color: '#D4A847', minWidth: '120px' },
  agentLineValue: { color: 'rgba(255,255,255,0.85)', fontWeight: '300', lineHeight: '1.5' },
};
